import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { departmentsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './DepartmentsPage.css';

export default function DepartmentsPage() {
  const { isEditor } = useAuth();
  const [departments, setDepartments]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [editing, setEditing]             = useState(null);   // dept object | null
  const [form, setForm]                   = useState({ name: '', description: '' });
  const [saving, setSaving]               = useState(false);
  const [deleteTarget, setDeleteTarget]   = useState(null);

  const loadDepartments = async () => {
    try {
      const res = await departmentsAPI.getAll();
      setDepartments(res.data.departments);
    } catch {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDepartments(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setShowModal(true);
  };

  const openEdit = (dept) => {
    setEditing(dept);
    setForm({ name: dept.name, description: dept.description || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        const res = await departmentsAPI.update(editing.id, form);
        setDepartments((prev) =>
          prev.map((d) => d.id === editing.id ? res.data.department : d)
        );
        toast.success('Department updated');
      } else {
        const res = await departmentsAPI.create(form);
        setDepartments((prev) => [...prev, res.data.department].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Department created');
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await departmentsAPI.delete(deleteTarget.id);
      setDepartments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 40 }}>
      <div className="page-header flex-between">
        <div>
          <h1>Departments</h1>
          <p className="text-muted">Manage the departments that appear in the feedback form.</p>
        </div>
        {isEditor && (
          <button className="btn btn-primary" onClick={openCreate}>
            + Add Department
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex-center" style={{ height: 200 }}><div className="spinner" /></div>
      ) : (
        <div className="dept-grid">
          {departments.map((dept, i) => (
            <div
              key={dept.id}
              className="dept-item card animate-fade"
              style={{ animationDelay: `${i * 0.04}s` }}
            >
              <div className="dept-item-body">
                <div className="dept-item-icon">{getDeptEmoji(dept.name)}</div>
                <div className="dept-item-info">
                  <h3 className="dept-item-name">{dept.name}</h3>
                  {dept.description && (
                    <p className="dept-item-desc text-muted text-small">{dept.description}</p>
                  )}
                  <span className="text-small text-muted">
                    Added {new Date(dept.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {isEditor && (
                <div className="dept-item-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(dept)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(dept)}>Delete</button>
                </div>
              )}
            </div>
          ))}

          {departments.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🏢</div>
              <h3>No departments yet</h3>
              <p className="text-muted">Add your first department to get started.</p>
              {isEditor && (
                <button className="btn btn-primary mt-4" onClick={openCreate}>Add Department</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal animate-scale" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Department' : 'Add Department'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Engineering"
                  autoFocus
                />
              </div>
              <div className="form-group mt-4">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description (optional)"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal animate-scale" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Department</h3>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>?</p>
              <p className="text-muted text-small mt-2">This will also remove all associated feedback entries.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      <br />
      <div><p className="login-footer">
              &copy; 2026 All Rights Reserved. <br />Credits to <a href="https://www.linkedin.com/in/narasimhapuli/" target='_blank'>V L Narasimha Rao PULI</a><br /><strong>Digital Applications Development Unit</strong> <br />BITS Pilani, Hyderabad Campus.
            </p></div>
    </div>
  );
}

function getDeptEmoji(name) {
  const n = name.toLowerCase();
  if (n.includes('engineer') || n.includes('tech')) return '⚙️';
  if (n.includes('hr') || n.includes('human'))       return '👥';
  if (n.includes('finance') || n.includes('account')) return '💰';
  if (n.includes('market'))                           return '📣';
  if (n.includes('sales'))                            return '📈';
  if (n.includes('support') || n.includes('customer')) return '🎧';
  if (n.includes('operat') || n.includes('admin'))   return '🏢';
  if (n.includes('product'))                         return '🚀';
  return '🏬';
}
